import React, { useCallback, useEffect, useState } from 'react';
import threadService from '../../services/threadService';
import styles from './ThreadModerationLogs.module.css';

const ACTION_LABELS = {
  status_changed: 'Statut',
  sticky_changed: 'Épinglage',
  forum_moved: 'Déplacement',
  rp_status_changed: 'Statut RP',
  theme_changed: 'Thème',
  thread_deleted: 'Suppression',
};

const formatLogDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ThreadModerationLogs = ({ threadId, refreshKey = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  const loadLogs = useCallback(async (pageToLoad = 1) => {
    if (!threadId) return;
    setLoading(true);
    setError('');
    try {
      const data = await threadService.getModerationLogs(threadId, {
        page: pageToLoad,
        limit: 15,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setPagination(data?.pagination || null);
      setPage(pageToLoad);
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible de charger le journal de modération');
      setItems([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    if (!isOpen) return;
    loadLogs(page);
  }, [isOpen, loadLogs, page, refreshKey]);

  const total = pagination?.total ?? 0;

  return (
    <section className={styles.panel} aria-label="Journal de modération">
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <span className={styles.toggleTitle}>Journal de modération</span>
        <span className={styles.toggleMeta}>
          {total > 0 ? `${total} entrée${total > 1 ? 's' : ''}` : 'Aucune entrée'}
        </span>
        <span className={`${styles.toggleChevron} ${isOpen ? styles.toggleChevronOpen : ''}`}>▾</span>
      </button>

      {isOpen && (
        <div className={styles.content}>
          {loading && <p className={styles.hint}>Chargement…</p>}
          {error && <p className={styles.error}>{error}</p>}
          {!loading && !error && items.length === 0 && (
            <p className={styles.hint}>Aucune action de modération enregistrée pour ce thread.</p>
          )}
          {!loading && !error && items.length > 0 && (
            <ul className={styles.list}>
              {items.map((entry) => (
                <li key={entry.id} className={styles.item}>
                  <div className={styles.itemHeader}>
                    <span className={styles.itemAction}>
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                    <time className={styles.itemDate} dateTime={entry.createdAt}>
                      {formatLogDate(entry.createdAt)}
                    </time>
                  </div>
                  <p className={styles.itemSummary}>{entry.summary}</p>
                  <p className={styles.itemActor}>
                    Par {entry.actor?.pseudo || 'Modérateur'}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {pagination && pagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={loading || page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Précédent
              </button>
              <span className={styles.pageInfo}>
                Page {pagination.page}/{pagination.totalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={loading || page >= pagination.totalPages}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Suivant
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ThreadModerationLogs;
